/**
 * Derivation behind `RatificationNextSteps` — the four facts the publish gate
 * reads, and the checklist those facts imply.
 *
 * Kept apart from the component so the checklist and the governance page's
 * Publish button share one source of truth about *why* publishing is blocked
 * (findings F4, docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { isTerminalLoopState } from '../../../lib/catchballLoopStates';
import { useUserStore } from '../../../stores/prioritization/userStore';

export type RatificationStepKey = 'accept' | 'take_ball' | 'close_loop' | 'publish';

export interface RatificationStep {
  key: RatificationStepKey;
  label: string;
  /** One line describing what the step does, in the user's terms. */
  description: string;
  done: boolean;
  /** Why this step cannot be taken yet — null when it is actionable or done. */
  blockedReason: string | null;
  /** Where the step is performed, when it is performed elsewhere. */
  href?: string;
  /** In-place action, when the step can be taken from here. */
  actionLabel?: string;
}

export interface LoopFacts {
  loopState: string | null;
  loopClosedAt: string | null;
  ballHolderUserId: string | null;
  iterationStatus: string | null;
  prioritySetState: string | null;
  hasLoop: boolean;
}

const EMPTY_FACTS: LoopFacts = {
  loopState: null,
  loopClosedAt: null,
  ballHolderUserId: null,
  iterationStatus: null,
  prioritySetState: null,
  hasLoop: false,
};

/**
 * Read the four facts the gates read. One place, so the checklist and the
 * Publish button can never disagree about why publishing is blocked.
 */
async function loadLoopFacts(loopId: string): Promise<LoopFacts> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || !loopId) return EMPTY_FACTS;

  const [loopRes, iterRes, setRes] = await Promise.all([
    supabase
      .from('catchball_loops')
      .select('state, closed_at, ball_holder_user_id')
      .eq('id', loopId)
      .maybeSingle(),
    supabase
      .from('egap_iterations')
      .select('status')
      .eq('catchball_loop_id', loopId)
      .order('iteration_index', { ascending: false })
      .limit(1),
    supabase
      .from('priority_sets')
      .select('state')
      .eq('loop_id', loopId)
      .order('created_at', { ascending: false })
      .limit(1),
  ]);

  const loop = loopRes.data as
    | { state: string | null; closed_at: string | null; ball_holder_user_id: string | null }
    | null;
  const iterations = (iterRes.data ?? []) as { status: string | null }[];
  const sets = (setRes.data ?? []) as { state: string | null }[];

  return {
    loopState: loop?.state ?? null,
    loopClosedAt: loop?.closed_at ?? null,
    ballHolderUserId: loop?.ball_holder_user_id ?? null,
    iterationStatus: iterations[0]?.status ?? null,
    prioritySetState: sets[0]?.state ?? null,
    hasLoop: loop != null,
  };
}

export function deriveRatificationSteps(
  facts: LoopFacts,
  userId: string | null,
  cycleId: string,
  loopId: string,
): RatificationStep[] {
  const published = facts.prioritySetState === 'published';
  const ratified = published || facts.prioritySetState === 'ratified';
  // Migration 692 makes a full accept set the loop 'accepted' and mark the
  // EGAp iteration accepted, so accept is done the moment either lands.
  const accepted =
    ratified ||
    facts.iterationStatus === 'accepted' ||
    facts.loopState === 'accepted' ||
    isTerminalLoopState(facts.loopState);
  const loopClosed = isTerminalLoopState(facts.loopState);
  const heldByMe = Boolean(userId) && facts.ballHolderUserId === userId;
  // Closing is what the publish gate wants; once closed, who held the ball on
  // the way there stops mattering.
  const ballTaken = heldByMe || loopClosed;

  return [
    {
      key: 'accept',
      label: 'Accept the proposal',
      description: 'Ratifies the ranked list and writes the new version.',
      done: accepted,
      blockedReason: null,
      href: accepted ? undefined : '/prioritization/cascade/inbox',
    },
    {
      key: 'take_ball',
      label: 'Take the returning ball',
      description: loopClosed
        ? 'The ball came back and the loop is closed.'
        : facts.ballHolderUserId && !heldByMe
          ? 'Someone else currently holds the ball on this loop.'
          : 'Custody returns to you so you can close the loop.',
      done: ballTaken,
      blockedReason: accepted ? null : 'Accept the proposal first.',
      href: `/prioritization/cascade/catchball/${cycleId}/${loopId}`,
      actionLabel: 'Take the ball',
    },
    {
      key: 'close_loop',
      label: 'Close the loop',
      description: 'Ends the exchange — the last thing publishing waits on.',
      done: loopClosed,
      blockedReason: !accepted
        ? 'Accept the proposal first.'
        : !ballTaken
          ? 'Take the ball first — only the holder can close the loop.'
          : null,
      href: `/prioritization/cascade/catchball/${cycleId}/${loopId}`,
      actionLabel: 'Close the loop',
    },
    {
      key: 'publish',
      label: 'Publish priorities',
      description: 'Makes the ratified list the organization’s published priorities.',
      done: published,
      blockedReason: !ratified
        ? 'The priority list is not ratified yet.'
        : !loopClosed
          ? 'Close the loop first.'
          : facts.iterationStatus != null && facts.iterationStatus !== 'accepted'
            ? `The EGAp iteration is still ${facts.iterationStatus}.`
            : null,
      href: `/prioritization/cycles/${cycleId}/governance`,
      actionLabel: 'Go to governance',
    },
  ];
}

export interface UseRatificationStepsResult {
  steps: RatificationStep[];
  isLoading: boolean;
  /** True when every gate publishing checks is satisfied. */
  canPublish: boolean;
  /** Why publishing is blocked, when it is. */
  publishBlockedReason: string | null;
  /** All four steps done — nothing left to wayfind. */
  isComplete: boolean;
  refresh: () => Promise<void>;
}

export function useRatificationSteps(
  cycleId: string | null | undefined,
  loopId: string | null | undefined,
): UseRatificationStepsResult {
  const userId = useUserStore((s) => s.userId);
  const [facts, setFacts] = useState<LoopFacts>(EMPTY_FACTS);
  const [isLoading, setIsLoading] = useState(Boolean(loopId));

  const refresh = useCallback(async () => {
    if (!loopId) {
      setFacts(EMPTY_FACTS);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setFacts(await loadLoopFacts(loopId));
    } finally {
      setIsLoading(false);
    }
  }, [loopId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const steps = useMemo(
    () => deriveRatificationSteps(facts, userId ?? null, cycleId ?? '', loopId ?? ''),
    [facts, userId, cycleId, loopId],
  );

  const publishStep = steps.find((s) => s.key === 'publish')!;

  return {
    steps,
    isLoading,
    canPublish: facts.hasLoop ? publishStep.blockedReason == null : true,
    publishBlockedReason: facts.hasLoop ? publishStep.blockedReason : null,
    isComplete: steps.every((s) => s.done),
    refresh,
  };
}

